/*
  Power Schedule codec — translates between the drag-paint widget's
  Uint8Array(672) internal state and CloudBolt's stored envelope:

    {"on":{"0":[[hour,slot],...],...,"6":[]},
     "off":{"0":[[hour,slot],...],...,"6":[]},
     "tz":"<IANA timezone>"}

  Day keys "0".."6" are Mon..Sun, hour ∈ [0,23], slot ∈ {0,15,30,45}.
  This shape is what `validate_power_schedule` emits and what
  `save_power_schedule_from_times` consumes (see
  src/infrastructure/models.py and src/infrastructure/views.py).

  The Uint8Array(672) is day-major: index = dayIndex*96 + slotIndex,
  dayIndex 0..6 = Mon..Sun, slotIndex 0..95 = 00:00..23:45 in 15-min steps.

  encode() walks the week as a *cycle* (seeded with slots[671]) so a
  schedule that spans Sun 22:00 → Mon 04:00 is one continuous "on" run
  with a single on-event on day 6 at 22:00 and a single off-event on
  day 0 at 04:00 — no spurious midnight transitions.

  This file is the single source of truth for the codec. In the
  browser (concat'd into c2.min.js by Gulp) it attaches to
  `c2.powerScheduleCodec`. Under Node (Vitest) it also exports via
  `module.exports` so the spec imports the same code that ships.
  Order in the c2 bundle: this file's alphabetical name means it
  loads before `power_schedule_widget.js`, so the widget can rely on
  `c2.powerScheduleCodec` being present.
*/

(function () {
  "use strict";

  var SLOTS_PER_DAY = 96;
  var DAYS = 7;
  var TOTAL_SLOTS = SLOTS_PER_DAY * DAYS; // 672
  var MINUTES_PER_SLOT = 15;

  function emptyDayMap() {
    var out = {};
    for (var d = 0; d < DAYS; d++) out[String(d)] = [];
    return out;
  }

  function defaultTimezone() {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    } catch (_e) {
      return "UTC";
    }
  }

  function hourOf(slot) {
    return Math.floor(slot / 4);
  }

  function minuteOf(slot) {
    return (slot % 4) * MINUTES_PER_SLOT;
  }

  /*
    encode(slots, tz?) — Uint8Array(672) -> envelope JSON string.

    Type-checks its argument so a mistaken caller fails loudly instead
    of writing garbage into the DB. Throws TypeError on bad shape.
  */
  function encode(slots, tz) {
    if (!(slots instanceof Uint8Array) || slots.length !== TOTAL_SLOTS) {
      throw new TypeError(
        "encode: expected Uint8Array of length " +
          TOTAL_SLOTS +
          ", got " +
          (slots && slots.length)
      );
    }

    var on = emptyDayMap();
    var off = emptyDayMap();

    var prev = slots[TOTAL_SLOTS - 1] ? 1 : 0;
    for (var g = 0; g < TOTAL_SLOTS; g++) {
      var cur = slots[g] ? 1 : 0;
      if (cur !== prev) {
        var day = Math.floor(g / SLOTS_PER_DAY);
        var s = g % SLOTS_PER_DAY;
        var evt = [hourOf(s), minuteOf(s)];
        if (cur === 1) {
          on[String(day)].push(evt);
        } else {
          off[String(day)].push(evt);
        }
      }
      prev = cur;
    }

    var timezone = tz || defaultTimezone();
    return JSON.stringify({ on: on, off: off, tz: timezone });
  }

  /*
    decode(jsonStr) -> {slots, tz}. Empty or malformed input returns a
    zeroed Uint8Array and a default tz — never throws.
  */
  function decode(jsonStr) {
    var slots = new Uint8Array(TOTAL_SLOTS);
    var fallbackTz = defaultTimezone();
    if (!jsonStr || typeof jsonStr !== "string") {
      return { slots: slots, tz: fallbackTz };
    }
    var parsed;
    try {
      parsed = JSON.parse(jsonStr);
    } catch (_e) {
      return { slots: slots, tz: fallbackTz };
    }
    if (!parsed || typeof parsed !== "object") {
      return { slots: slots, tz: fallbackTz };
    }
    var events = [];
    var onMap = parsed.on || {};
    var offMap = parsed.off || {};
    for (var d = 0; d < DAYS; d++) {
      var onList = onMap[String(d)] || [];
      var offList = offMap[String(d)] || [];
      var i;
      for (i = 0; i < onList.length; i++) {
        var oh = onList[i][0];
        var om = onList[i][1];
        var os = oh * 4 + Math.floor(om / MINUTES_PER_SLOT);
        if (os >= 0 && os < SLOTS_PER_DAY) {
          events.push({ g: d * SLOTS_PER_DAY + os, dir: 1 });
        }
      }
      for (i = 0; i < offList.length; i++) {
        var fh = offList[i][0];
        var fm = offList[i][1];
        var fs = fh * 4 + Math.floor(fm / MINUTES_PER_SLOT);
        if (fs >= 0 && fs < SLOTS_PER_DAY) {
          events.push({ g: d * SLOTS_PER_DAY + fs, dir: 0 });
        }
      }
    }
    if (events.length === 0) {
      return { slots: slots, tz: parsed.tz || fallbackTz };
    }
    events.sort(function (a, b) {
      return a.g - b.g;
    });
    var state = events[events.length - 1].dir;
    var eIdx = 0;
    for (var g2 = 0; g2 < TOTAL_SLOTS; g2++) {
      while (eIdx < events.length && events[eIdx].g === g2) {
        state = events[eIdx].dir;
        eIdx++;
      }
      slots[g2] = state;
    }
    return { slots: slots, tz: parsed.tz || fallbackTz };
  }

  function presetClear() {
    return new Uint8Array(TOTAL_SLOTS);
  }

  function presetWeekdays9to5() {
    var s = new Uint8Array(TOTAL_SLOTS);
    // Mon-Fri (days 0..4), slots 36..67 (09:00..16:45 painted on;
    // off transition at slot 68 = 17:00).
    for (var d = 0; d <= 4; d++) {
      for (var x = 36; x < 68; x++) s[d * SLOTS_PER_DAY + x] = 1;
    }
    return s;
  }

  function presetEveryDay9to5() {
    var s = new Uint8Array(TOTAL_SLOTS);
    for (var d = 0; d <= 6; d++) {
      for (var x = 36; x < 68; x++) s[d * SLOTS_PER_DAY + x] = 1;
    }
    return s;
  }

  var codec = {
    encode: encode,
    decode: decode,
    presetClear: presetClear,
    presetWeekdays9to5: presetWeekdays9to5,
    presetEveryDay9to5: presetEveryDay9to5,
    SLOTS_PER_DAY: SLOTS_PER_DAY,
    DAYS: DAYS,
    TOTAL_SLOTS: TOTAL_SLOTS,
    MINUTES_PER_SLOT: MINUTES_PER_SLOT,
    // Internal helpers exposed for the widget's UI-side label rendering.
    hourOf: hourOf,
    minuteOf: minuteOf,
  };

  // Browser (c2.min.js) — attach to c2 namespace.
  if (typeof window !== "undefined") {
    window.c2 = window.c2 || {};
    window.c2.powerScheduleCodec = codec;
  }

  // Node / Vitest — expose the same object via CommonJS so the spec
  // imports what actually ships.
  if (typeof module !== "undefined" && module.exports) {
    module.exports = codec;
  }
})();
